/**
 * Data-saver policy: WHAT to watch at each level, and WHEN to move between
 * levels. Pure functions — the room side effects live in useDataSaver.ts.
 *
 * The ladder protects the class in the order a lesson actually degrades on a
 * congested Cairo connection:
 *
 *   L0 full      — everything (adaptiveStream already right-sizes tiles)
 *   L1 focus     — peer students' cameras unsubscribed; teacher video, screen
 *                  share and ALL audio stay. For a HOST of a small class this
 *                  level keeps student cameras too — see wantsCamera.
 *   L2 essentials— every remote camera unsubscribed; the screen share STAYS but
 *                  its simulcast layer is capped to the 720p mid rung (LK-027); audio stays
 *
 * Audio is never touched at any level: audio breaking up is the class failing.
 * The share is never UNSUBSCRIBED or PAUSED at any level — the share IS the
 * lesson — but at L2 its subscribed layer is capped down (see
 * shareVideoQuality), which is a degrade, never a drop. There is deliberately
 * no "audio-only" level here — if even the share cannot survive, WebRTC's own
 * simulcast/degradation is already doing the last-resort work, and the
 * reconnect flow owns true outages.
 *
 * A second, quiet win: when every subscriber drops a student's camera, dynacast
 * stops ENCODING it on that student's phone — the downlink policy hands uplink
 * and CPU back to the exact devices that need it.
 */

export type SaverLevel = 0 | 1 | 2

/** Simplified network signal fed to the machine. */
export type QualitySignal = 'ok' | 'poor' | 'unknown'

/**
 * Hysteresis timings, retuned after the first real cohort (2026-08-01).
 *
 * Degrading is *very* reluctant: the first cohort's tutors lost the student's
 * camera after 8 s of a "Poor" score they experienced as a stable call, and
 * read it as the product breaking. LiveKit's quality score is pessimistic and
 * twitchy on Egyptian consumer links, so the ladder now demands a poor run
 * long enough that a human would ALSO call the connection bad.
 *
 * Recovery is quicker AND forgiving: the old machine required 45 s of
 * uninterrupted `ok`, and a single poor sample reset that clock — on a link
 * whose score flaps every half-minute the ladder literally never came back,
 * which is why "I had to refresh to see the student again" was the complaint.
 * Now a poor run shorter than BLIP_FORGIVE_MS keeps the recovery credit.
 */
export const POOR_TO_L1_MS = 20_000
/**
 * A FRESH poor run beyond L1 — so L2 needs 80 s of bad link, not 45 s. L2 is
 * the nuclear level (every camera in the class goes, including the tutor's),
 * and the signal driving it is a server-side score dominated by the local
 * UPLINK — not proof that this client cannot receive. Blunt enough that it
 * should have to insist.
 */
export const POOR_TO_L2_MS = 60_000
export const RECOVER_STEP_MS = 30_000
/** A poor run shorter than this is a blip: it does not reset recovery credit. */
export const BLIP_FORGIVE_MS = 10_000
/** How often the hook re-evaluates while degraded or while quality is poor. */
export const TICK_MS = 5_000
/**
 * After "Resume videos", how long the link must hold GOOD before the ladder is
 * allowed to judge again. Not a countdown: the override lasts as long as the
 * bad episode does, so videos can never blink off again mid-lesson while the
 * tutor is still living through the same bad patch they just overrode.
 */
export const OVERRIDE_CLEAR_MS = 15_000
/**
 * A Host watching this many remote cameras or fewer keeps them at L1: in a
 * 1:1 the student IS the lesson, and unsubscribing their camera saves a few
 * hundred kbps at catastrophic teaching cost. L2 still drops everything.
 */
export const SMALL_CLASS_MAX_CAMERAS = 3

export interface AutoState {
  level: SaverLevel
  /** When the current uninterrupted run of poor quality started. */
  poorSince: number | null
  /**
   * When the current run of recovery credit started. Unlike poorSince this
   * SURVIVES short poor blips (see BLIP_FORGIVE_MS) — both clocks can be set
   * at once while a blip is being waited out.
   */
  goodSince: number | null
}

export const INITIAL_AUTO_STATE: AutoState = { level: 0, poorSince: null, goodSince: null }

/**
 * Coming OUT of an `unknown` stretch (reconnects report it while wall time
 * keeps moving), the active clock must restart at `now` — otherwise the
 * unknown gap silently counts toward escalation or recovery and the ladder
 * moves on evidence it never had. Post-reconnect, pre-gap credit is stale
 * evidence in BOTH directions, so the opposite clock is dropped too.
 */
export function rebaseClocks(state: AutoState, quality: QualitySignal, now: number): AutoState {
  if (quality === 'poor') return { ...state, poorSince: now, goodSince: null }
  if (quality === 'ok') return { ...state, poorSince: null, goodSince: now }
  return state
}

/**
 * Advance the auto ladder given the latest quality signal at time `now`.
 *
 * Escalates one level after a sustained poor run; recovers one level after a
 * sustained run of recovery credit, where credit survives poor blips shorter
 * than BLIP_FORGIVE_MS; `unknown` freezes both clocks (no evidence either way —
 * LiveKit reports it briefly around joins and reconnects).
 */
export function stepAuto(state: AutoState, quality: QualitySignal, now: number): AutoState {
  if (quality === 'unknown') return state

  if (quality === 'poor') {
    const poorSince = state.poorSince ?? now
    const runMs = now - poorSince
    // Recovery credit survives a short blip; a real poor run forfeits it.
    const goodSince = runMs >= BLIP_FORGIVE_MS ? null : state.goodSince
    if (state.level === 0 && runMs >= POOR_TO_L1_MS) {
      // Restart the clock: L2 requires a fresh sustained run beyond L1.
      return { level: 1, poorSince: now, goodSince: null }
    }
    if (state.level === 1 && runMs >= POOR_TO_L2_MS) {
      return { level: 2, poorSince: now, goodSince: null }
    }
    // Same-reference when nothing changed, so React state consumers can bail.
    if (state.poorSince === poorSince && state.goodSince === goodSince) return state
    return { ...state, poorSince, goodSince }
  }

  // quality === 'ok'
  // A blip we waited out PAUSES the recovery clock rather than resetting it:
  // the credit start moves forward by the blip's length, so credit only ever
  // counts time that was actually good — a link flapping poor/ok gains
  // nothing from the forgiveness, it just stops losing everything to it.
  const goodSince =
    state.goodSince === null
      ? now
      : state.poorSince !== null
        ? Math.min(now, state.goodSince + (now - state.poorSince))
        : state.goodSince
  if (state.level > 0 && now - goodSince >= RECOVER_STEP_MS) {
    // Stepwise recovery, clock restarted per step — L2 → L0 takes two full
    // good runs, which is the point.
    return { level: (state.level - 1) as SaverLevel, poorSince: null, goodSince: now }
  }
  if (state.goodSince === goodSince && state.poorSince === null) return state
  return { ...state, poorSince: null, goodSince }
}

/**
 * The highest simulcast layer the SUBSCRIBED screen share may be served at
 * each level (LK-027).
 *
 * The share is the lesson, so it is never dropped and never paused — but the
 * 2.5 Mbps top share layer is the single largest data cost in the room, and
 * L2 is where the saver has already taken every camera (manual mode lands
 * here too). Capping the subscription to the mid rung (720p@5, ~800 kbps)
 * keeps text readable instead of paying full bitrate; the low rung (360p@3) is
 * a 3x downscale of the tutor's screen and was unreadable. L1 stays untouched:
 * downgrading the share for a flap would punish the lesson for nothing.
 *
 * 'high' is a CAP, not a request: the SDK serves min(cap, adaptive size), so
 * HIGH alone never raises a share above its on-screen box. useScreenSharePin
 * raises the adaptive request to the published size so this cap governs.
 *
 * HARD CONSTRAINT (LK-015): callers must apply this as setVideoQuality ONLY —
 * a layer cap on the live subscription. Never unsubscribe or pause the
 * share; useScreenSharePin pins it visible precisely to avoid black screens,
 * and a lower rung must stay compatible with that pin.
 */
export function shareVideoQuality(level: SaverLevel): 'medium' | 'high' {
  return level === 2 ? 'medium' : 'high'
}

/**
 * Does this level want a given remote camera subscribed?
 * Screen share and microphones are NOT ruled by this — callers never pass them.
 *
 * `keepStudents` is the small-class Host exemption (computed by the hook):
 * at L1 a tutor watching a handful of students keeps them all — for the
 * teacher the students are the lesson, exactly as the teacher is for them.
 */
export function wantsCamera(
  level: SaverLevel,
  publisherIsTeacher: boolean,
  keepStudents = false,
): boolean {
  if (level === 0) return true
  if (level === 1) return publisherIsTeacher || keepStudents
  return false
}
